import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Dropzone } from '@dropzone-ui/react';
import { ClientContext, ProfileContext } from '../../Contexts/contexts';
import { shouldFetchData, status } from '../../../data/fetchStatus';
import HeaderMinimal from '../../Shared/Headers/HeaderMinimal';
import SideMenu from '../../Splitter/SideMenu/SideMenu';
import ShareDocumentsElement from './ShareDocumentsElement';
import {
  download_icon_small,
  download_icon,
  folder_image,
} from '../../../utils/images';
import { quoteStatus } from '../../../data/quoteStatus';
import Services from '../../Services/services';
import useToast from '../../Toasts/useToast';
import {
  ToastHeaders,
  ToastThemes,
  ToastMessages,
} from '../../Toasts/ToastOptions/toastOptions';
import useAuth from '../../Contexts/useAuth';

function ShareDocuments() {
  const clientContext = useContext(ClientContext);
  const profileContext = useContext(ProfileContext);
  const enqueueToast = useToast();
  const [documents, setDocuments] = useState([]);
  const [documentsFetched, setDocumentsFetched] = useState(status.DEFAULT);
  const { checkAuth, validateRoleRoute } = useAuth();
  const ref = useRef(true);

  const fetchDocuments = useCallback(async () => {
    if (
      documentsFetched === status.FETCHED ||
      clientContext?.clientId === undefined
    )
      return;

    setDocumentsFetched(status.FETCHED);

    try {
      const documentsData = await Services.documentService.documentsByClientID(
        clientContext?.clientId
      );

      setDocuments(documentsData);
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }
  }, [clientContext?.clientId, documentsFetched, enqueueToast]);

  useEffect(() => {
    if (checkAuth()) {
      const firstRendering = ref.current;
      if (validateRoleRoute('client')) {
        if (firstRendering) {
          ref.current = false;
          clientContext.refreshClientExpertRelations();
        }

        if (shouldFetchData(documentsFetched)) fetchDocuments();
      }
    }
  }, [
    documentsFetched,
    fetchDocuments,
    checkAuth,
    validateRoleRoute,
    clientContext,
  ]);

  async function onUploadItem(files) {
    if (files.length === 0) return;

    try {
      const fileNames = files.map(
        (file) => `${Date.now()}_${file.name.replace(/\s/g, '')}`
      );

      const uploadLinks = await Services.documentService.generateUploadLinks(
        fileNames
      );

      for (const [idx, value] of files.entries()) {
        await Services.documentService.uploadFile(value.file, uploadLinks[idx]);
      }

      const promises = [];
      files.forEach((idx, file) => {
        promises.push(
          Services.documentService.createDocumentUploaded(
            file.name,
            fileNames[idx],
            'ab568b24-92b9-4211-ab63-6d00fce02277',
            clientContext?.clientId,
            clientContext?.clientExpertRelation?.expertId,
            'client',
            'client share document'
          )
        );
      });

      await Promise.all(promises);

      setDocumentsFetched(status.REFRESH);

      enqueueToast(ToastMessages.CONFIRM_SUCCESS_APPOINTMENT, {
        theme: ToastThemes.toastThemes.CONFIRM_SUCCESS,
        header: ToastHeaders[ToastThemes.toastThemes.CONFIRM_SUCCESS],
        duration: 3000,
      });
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
      console.error(error);
    }
  }

  // Pour supprimer un file uploaded
  async function onDeleteItem(id, fileKey) {
    try {
      await Services.documentService.deleteFile(fileKey);

      await Services.documentService.deleteDocumentUploadedByID(id);

      setDocumentsFetched(status.REFRESH);

      enqueueToast(ToastMessages.CONFIRM_SUCCESS_APPOINTMENT, {
        theme: ToastThemes.toastThemes.CONFIRM_SUCCESS,
        header: ToastHeaders[ToastThemes.toastThemes.CONFIRM_SUCCESS],
        duration: 3000,
      });
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }
  }

  // Fonction qui permet d'afficher les documents partages
  function getAllRawDocumentsElements() {
    const shareDocumentElementsItems = [];

    documents.forEach((document, index) => {
      shareDocumentElementsItems.push(
        <ShareDocumentsElement
          id={index + '-we'}
          key={index + '-we'}
          data={document}
          onDeleteItem={(id) => onDeleteItem(id, document.fileKey)}
        />
      );
    });

    return shareDocumentElementsItems;
  }

  return (
    <>
      <HeaderMinimal />
      {profileContext.isClient && !profileContext.fetchingClient && (
        <div className="ts-container">
          <SideMenu />
          <div className="ts-container-column">
            <div className="ts-details-container">
              <div className="ts-details">
                <div className="ts-title">Partager document</div>
                <div className="ts-resume">
                  Nous mettons à votre disposition la possibilité de partager
                  des documents avec votre expert avant votre rendez-vous,
                  permettant ainsi une meilleure compréhension de votre
                  entreprise.
                  <br />
                  N'hésitez pas à lui fournir les informations que vous jugez
                  pertinentes.
                </div>
              </div>
              <div className="ts-content">
                <div className="ts-content-container">
                  <div className="align-center-title">Partager documents</div>
                </div>
                <img
                  className="ts-icon grey-icon"
                  src={download_icon}
                  alt="download icon"
                ></img>
                <div className="grey-rod"></div>
              </div>
            </div>
            {clientContext?.clientExpertRelation !== undefined &&
              clientContext?.clientExpertRelation?.status >=
                quoteStatus.clientAcceptAppointment && (
                <div className="sd-container-main">
                  {documents.length > 0 ? (
                    <div className="sd-container-list">
                      <div className="sd-elements-container">
                        {getAllRawDocumentsElements()}
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}

                  <div className="sd-container-dropzone">
                    <Dropzone
                      color="#4188F2"
                      onChange={(files) => onUploadItem(files)}
                      header={false}
                      footer={false}
                      style={{
                        fontFamily: 'Poppins regular',
                        fontSize: '14px',
                        border: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='14' ry='14' stroke='%234188F2FF' stroke-width='2' stroke-dasharray='14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
                      }}
                    >
                      <div className="sd-container-img-label">
                        <img
                          className="img-folder"
                          src={folder_image}
                          alt="folder"
                        ></img>
                        <img
                          className="img-dwnld"
                          src={download_icon_small}
                          alt="downlad icon small"
                        ></img>
                        <div className="sd-dropzone-title">
                          Envoyer des documents
                        </div>
                        <div className="sd-dropzone-text">
                          Vous pouvez déposer ici vos documents que vous
                          souhaitez partager avec l'expert.
                        </div>
                      </div>
                    </Dropzone>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </>
  );
}

export default ShareDocuments;
